import React, { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, Loader2, CheckCircle } from 'lucide-react';

const ImageUploadV2 = ({
    onUploadSuccess,
    onUploadError,
    uploadEndpoint = '/api/v2/events/upload/event-background',
    currentImageUrl = null,
    label = 'Upload Image',
    accept = 'image/*'
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [previewUrl, setPreviewUrl] = useState(currentImageUrl);
    const [uploadedUrl, setUploadedUrl] = useState(currentImageUrl);
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            handleFile(files[0]);
        }
    };

    const handleFileSelect = (e) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            handleFile(files[0]);
        }
    };

    const handleFile = async (file) => {
        // Validate file type
        if (!file.type.startsWith('image/')) {
            setError('Please select an image file');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            setError('File size must be less than 5MB');
            return;
        }

        setError(null);

        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
            setPreviewUrl(e.target.result);
        };
        reader.readAsDataURL(file);

        // Upload to S3
        await uploadFile(file);
    };

    const uploadFile = async (file) => {
        setIsUploading(true);
        setUploadProgress(0);

        const formData = new FormData();
        formData.append('image', file);

        try {
            const xhr = new XMLHttpRequest();

            // Track upload progress
            xhr.upload.addEventListener('progress', (e) => {
                if (e.lengthComputable) {
                    const progress = Math.round((e.loaded / e.total) * 100);
                    setUploadProgress(progress);
                }
            });

            // Handle completion
            xhr.addEventListener('load', () => {
                if (xhr.status === 200) {
                    const response = JSON.parse(xhr.responseText);
                    setUploadedUrl(response.url);
                    setIsUploading(false);
                    setUploadProgress(100);

                    if (onUploadSuccess) {
                        onUploadSuccess(response.url);
                    }
                } else {
                    const error = JSON.parse(xhr.responseText);
                    setError(error.message || 'Upload failed');
                    setIsUploading(false);

                    if (onUploadError) {
                        onUploadError(error);
                    }
                }
            });

            // Handle errors
            xhr.addEventListener('error', () => {
                setError('Network error occurred');
                setIsUploading(false);

                if (onUploadError) {
                    onUploadError({ message: 'Network error' });
                }
            });

            xhr.open('POST', `${process.env.REACT_APP_API_URL}${uploadEndpoint}`);
            xhr.send(formData);

        } catch (err) {
            setError(err.message || 'Upload failed');
            setIsUploading(false);

            if (onUploadError) {
                onUploadError(err);
            }
        }
    };

    const handleRemove = () => {
        setPreviewUrl(null);
        setUploadedUrl(null);
        setUploadProgress(0);
        setError(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        if (onUploadSuccess) {
            onUploadSuccess(null);
        }
    };

    return (
        <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-2">
                {label}
            </label>

            {!previewUrl ? (
                <div
                    className={`relative border-2 border-dashed rounded-lg p-6 transition-colors ${isDragging
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400'
                        }`}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={accept}
                        onChange={handleFileSelect}
                        className="hidden"
                    />

                    <div className="text-center">
                        <Upload className="mx-auto h-12 w-12 text-gray-400" />
                        <p className="mt-2 text-sm text-gray-600">
                            Drag and drop an image here, or{' '}
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="text-blue-600 hover:text-blue-700 font-medium"
                            >
                                browse
                            </button>
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                            PNG, JPG, GIF up to 5MB
                        </p>
                    </div>
                </div>
            ) : (
                <div className="relative border-2 border-gray-300 rounded-lg overflow-hidden">
                    <img
                        src={previewUrl}
                        alt="Preview"
                        className="w-full h-48 object-cover"
                    />

                    {isUploading && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col items-center justify-center">
                            <Loader2 className="h-8 w-8 text-white animate-spin mb-2" />
                            <div className="w-3/4 bg-gray-200 rounded-full h-2">
                                <div
                                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${uploadProgress}%` }}
                                />
                            </div>
                            <p className="text-white text-sm mt-2">{uploadProgress}%</p>
                        </div>
                    )}

                    {uploadedUrl && !isUploading && (
                        <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded-md flex items-center text-xs">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Uploaded
                        </div>
                    )}

                    <button
                        type="button"
                        onClick={handleRemove}
                        className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition-colors"
                        disabled={isUploading}
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            )}

            {error && (
                <p className="mt-2 text-sm text-red-600">{error}</p>
            )}

            {uploadedUrl && (
                <p className="mt-2 text-xs text-gray-500 truncate">
                    URL: {uploadedUrl}
                </p>
            )}
        </div>
    );
};

export default ImageUploadV2;
